import {
  ProfileCompletionResponse,
  JobStatsResponse,
  JobSearchParams,
  JobApplication,
  JobApplyRequest,
  Job,
  GetJobApplicationsParams,
  JobProfileCompletionRequest,
  SearchJobResponse,
  GetApplicationResponse,
  CalculateSimiliarityScoreResponse,
  InviteToApplyResponse,
  SearchApplicationJobRequest,
  JobSuggestedCandidateParams,
  StartupCandidateParams,
  JobListingFilter,
  JobHRListingParams,
  InviteToApplyRequest,
  ResendInvitationJobCollaboration,
  RemoveJobCollaboration,
  ShortlistInviteRequest,
  ApplicantUpdateStatusRequest,
  UpdateApplicantScheduleNote,
  AddMultipleApplicantScheduleNote,
  JobWorkAuthorizationMetadata,
  SurveyFormData,
} from "@type/job";
import { LocationMetadata } from "@service/employer";
import {
  ApplicationComment,
  CreateApplicationCommentRequest,
  UpdateApplicationCommentRequest,
} from "@type/applicant";
import axios, { AxiosResponse, AxiosError } from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog, fatalErrorLog, serializeRequestPayload } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

export interface JobCountsResponse {
  active: number;
  inactive: number;
}

export async function getJobCounts(): Promise<JobCountsResponse> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/hr/job-counts`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 20000,
    });

    return response.data.data;
  } catch (error) {
    const err = error as AxiosError;

    if (error instanceof Error) {
      catchErrorLog(`failed to get job counts, err: ${error.message}`);
    }
    throw err;
  }
}

export async function getListCompanies(): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/search/companies`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Fetches the profile completion status for the current user
 * @returns Promise with profile completion data
 */
export async function getProfileCompletion(): Promise<ProfileCompletionResponse> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/profile-completion`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000, // 10 second timeout
    });

    return response.data.data;
  } catch (error) {
    const err = error as AxiosError;
    console.error("Failed to fetch profile completion:", err);
    throw err;
  }
}

/**
 * Fetches job statistics for the current user
 * @returns Promise with job stats data
 */
export async function getJobStats(): Promise<JobStatsResponse> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/stats`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000, // 10 second timeout
    });

    return response.data.data;
  } catch (error) {
    const err = error as AxiosError;
    console.error("Failed to fetch job statistics:", err);

    // Return empty stats on error
    return {
      stats: [
        {
          title: "Job Matches",
          value: 0,
          subtitle: "Based on your profile",
          icon: "JobsIcon",
        },
        {
          title: "Applications",
          value: 0,
          subtitle: "0 in review",
          icon: "ApplicationsIcon",
        },
        {
          title: "Profile Views",
          value: 0,
          subtitle: "+0 this week",
          icon: "ProfileViewsIcon",
        },
      ],
    };
  }
}

/**
 * Finds job listings based on search criteria
 * @param params Optional search parameters (keyword, location, job_type)
 * @returns Promise with job listing data
 */
export async function findJobs(
  params?: JobSearchParams
): Promise<SearchJobResponse> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/search/jobs`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: params,
      timeout: 15000, // 15 second timeout
    });

    return {
      data: response.data.data?.jobs || [],
      meta: response.data.meta,
    };
  } catch (error) {
    const err = error as AxiosError;
    console.error("Failed to fetch job listings:", err);

    // Return empty array on error
    return {
      data: [],
      meta: {
        pagination: {
          total: 0,
          page: 1,
          limit: 15,
          total_pages: 0,
        },
      },
    };
  }
}

export async function getJobApplications(
  params: GetJobApplicationsParams = { page: 1, limit: 15 }
): Promise<GetApplicationResponse> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/applications`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: params,
      timeout: 10000, // 10 second timeout
    });

    // The API returns the applications array directly
    return {
      data: response.data.data || [],
      meta: response.data.meta,
    };
  } catch (error) {
    const err = error as AxiosError;
    console.error("Failed to fetch job applications:", err);

    // Return empty array on error
    return {
      data: [],
      meta: {
        pagination: {
          total: 0,
          page: 1,
          limit: 15,
          total_pages: 0,
        },
      },
    };
  }
}

/**
 * Apply for a job
 * @param payload - Job application data including job_id and optional additional_notes
 * @returns Promise with job application data
 */
export async function applyForJob(
  payload: JobApplyRequest
): Promise<JobApplication> {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/apply`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: payload,
      timeout: 10000, // 10 second timeout
    });

    return response.data.data;
  } catch (error) {
    const err = error as AxiosError;

    if (error instanceof Error) {
      catchErrorLog(`failed to apply job, err: ${error.message}`);
    }

    console.error("Failed to apply for job:", err);
    throw err;
  }
}

export async function getJobDetail(jobId: string): Promise<Job> {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/detail/${jobId}`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 30000, // 10 second timeout
    });

    return response.data.data;
  } catch (error) {
    const err = error as AxiosError;
    if (error instanceof Error) {
      catchErrorLog(`failed to job detail, err: ${error.message}`);
    }

    throw err;
  }
}

export async function getJobDetailGuest(jobId: string): Promise<Job> {
  try {
    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/open-detail/${jobId}`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data.data;
  } catch (error) {
    const err = error as AxiosError;

    if (error instanceof Error) {
      catchErrorLog(`failed to get job guest, err: ${error.message}`);
    }
    throw err;
  }
}

export async function jobProfileCompletion(
  body: JobProfileCompletionRequest
): Promise<boolean> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/profile-completion`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000, // 10 second timeout
      data: body,
    });

    if (response.status === 200) {
      return true;
    } else {
      throw new Error(`Unexpected response status: ${response.status}`);
    }
  } catch (error) {
    const err = error as AxiosError;
    console.error("Failed to call job profile completion:", err);
    return false;
  }
}

/**
 * Fetches job listings created by the current HR user
 * @param limit - Number of job listings to return, defaults to 5
 * @returns Promise with HR job listings data
 */
export async function getHrJobListings(params: JobHRListingParams): Promise<any> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/hr/listings`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: {
        keyword: params.keyword,
        limit: params.limit,
        page: params.page,
        job_status: params.status,
        job_visibility: params.visibility,
        filter_company: params.companyName,
        candidate_id: params.candidate_id,
        company_id: params.company_id,
        without_collaborators: params.without_collaborators
      },
    });

    return response;
  } catch (error) {
    const err = error as AxiosError;
    console.error("Failed to fetch HR job listings:", err);
    // Return empty array on error
    return undefined;
  }
}

/**
 * Fetches applicants for a specific job
 * @param jobId - The UUID of the job
 * @param limit - Number of applicants to return, defaults to 5
 * @returns Promise with job applicants data
 */
export async function getJobApplicants(
  params: SearchApplicationJobRequest
): Promise<any> {

  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/jobs/${params.job_id}/applicants`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params: {
      limit: params.limit,
      page: params.page,
      keyword: params.keyword,
      application_status: params.application_status,
      match_level: params.match_level,
      sort_by: params.sort_by,
      sort: params.sort,
      funnel_status: params.funnel_status,
      // #3201: NEVER send `flow_version` here by default. The BE treats it as
      // "only candidates pinned to this flow version", so passing the job's current
      // version hides everyone who ran an older version after HR edits a step. Omit
      // it so all versions are returned; only add it for an explicit user-driven
      // "flow version" filter (BE ourteam-backend#3904).
    },
    timeout: 15000, // 15 second timeout
  });
}

/**
 * Per-job WhatsApp ad deep link (#3088, BE ourteam-backend#3664). The `link` is a
 * ready-to-paste `wa.me` URL whose pre-filled message embeds a `[REF:<job_id>]`
 * code; a candidate who taps it starts this job's hiring flow on WhatsApp
 * (routing is entirely server-side). Copy `link` verbatim — never reconstruct it.
 */
export interface WhatsappAdLink {
  job_id: string;
  ref_code: string;
  wa_number: string;
  prefilled_text: string;
  link: string;
  // #3269 (BE #4012): short marketing link (link.ourteam.ai/wa/<code>), stable
  // per job. Prefer it when copying; absent on an older BE ⇒ fall back to `link`.
  short_link?: string;
  short_code?: string;
}

/** GET /jobs/{job_id}/whatsapp-ad-link — HR/admin only (job_seeker → 403). */
export async function getJobWhatsappAdLink(jobId: string): Promise<WhatsappAdLink> {
  const token = getCookie("authToken");
  const response: AxiosResponse = await axios({
    url: `${apiURL}/jobs/${jobId}/whatsapp-ad-link`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    timeout: 15000,
  });
  return response.data.data;
}

/**
 * A single candidate's similarity-score state from the lightweight poll endpoint.
 * `ready:false` means the BE is still computing (embeddings + LLM) — `similarity_score`
 * is `null` until `ready:true`.
 */
export interface ApplicantScore {
  candidate_id: string;
  similarity_score: number | null;
  ready: boolean;
  updated_at: string | null;
}

/**
 * Lightweight poll for applicant similarity scores.
 * Cheap (single DB read, no LLM/enrichment) — safe to poll every ~2s, unlike
 * `getJobApplicants`. Pass `candidateIDs` to scope the payload to the visible cards;
 * omit for all scored candidates on the job. When `candidateIDs` is passed, every
 * requested id is returned (missing → `ready:false`).
 * @param jobID - The UUID of the job
 * @param candidateIDs - Optional list of candidate ids to scope the response
 */
export async function getApplicantsScores(
  jobID: string,
  candidateIDs?: string[]
): Promise<ApplicantScore[]> {
  const token = getCookie("authToken");
  const response: AxiosResponse = await axios({
    url: `${apiURL}/jobs/${jobID}/applicants/scores`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params:
      candidateIDs && candidateIDs.length > 0
        ? { candidate_ids: candidateIDs.join(",") }
        : {},
    timeout: 15000,
  });

  return response.data.data;
}

/**
 * Updates an existing job posting
 * @param jobId - The UUID of the job to update
 * @param jobData - The updated job data
 * @returns Promise with updated job data
 */
export async function updateJob(jobId: string, jobData: any): Promise<any> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/${jobId}`,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: jobData,
      // set 2min timeout
      timeout: 120000,
    });

    return response.data.data;
  } catch (error) {
    const err = error as AxiosError;

    // Same filtered-message problem as createJob: `error.message` on an axios
    // failure is "Request failed with status code 401", which catchErrorLog
    // drops. This one carries two different losses depending on the caller —
    // a full edit loses the HR's job definition, and a status toggle
    // (pause / activate, via editJobStatus) silently does not apply — so the
    // payload's job_status is recorded to tell them apart.
    fatalErrorLog(
      "[job] update failed — the job changes were not saved",
      {
        job_id: jobId,
        job_status: (jobData as { job_status?: string } | undefined)?.job_status,
        http_status: err.response?.status ?? "none (network or timeout)",
        detail: (err.response?.data as { message?: string } | undefined)?.message || err.message,
        // The exact body that was sent. A rejected job save is usually a
        // payload problem (a field the BE now validates, an oversized
        // hiring_flow), and without the body that is guesswork.
        request_payload: serializeRequestPayload(jobData),
      },
      { area: "job_write" },
    );
    throw err;
  }
}

/**
 * Closes a job listing
 * @param jobId - The UUID of the job to close
 * @returns Promise with success status
 */
export async function closeJob(jobId: string): Promise<boolean> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/${jobId}/close`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 20000,
    });

    return response.status >= 200 && response.status < 300;
  } catch (error) {
    const err = error as AxiosError;

    if (error instanceof Error) {
      catchErrorLog(`failed to close job, err: ${error.message}`);
    }
    throw err;
  }
}

/**
 * Duplicates a job listing into a new job (#3443, BE #4483). The new job is
 * created Active immediately (product decision) and carries the source job's
 * full configuration but no applicants, stats, or version history.
 * @param jobId - The UUID of the source job
 * @returns Promise with the new job's id
 */
export async function duplicateJob(jobId: string): Promise<string> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/${jobId}/duplicate`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 20000,
    });

    return response.data?.data?.job_id;
  } catch (error) {
    const err = error as AxiosError;

    if (error instanceof Error) {
      catchErrorLog(`failed to duplicate job, err: ${error.message}`);
    }
    throw err;
  }
}

/**
 * Deletes a job posting
 * @param jobId - The UUID of the job to delete
 * @returns Promise with success status
 */
export async function deleteJob(jobId: string): Promise<boolean> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/${jobId}`,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 20000, // 20 second timeout
    });

    // Check if the request was successful (status code in the 2xx range)
    return response.status >= 200 && response.status < 300;
  } catch (error) {
    const err = error as AxiosError;

    if (error instanceof Error) {
      catchErrorLog(`failed to delete job, err: ${error.message}`);
    }
    throw err;
  }
}

export async function calculateSimiliarityScoreBatch(
  jobID: string,
  candidateIDs?: string[]
): Promise<CalculateSimiliarityScoreResponse[] | undefined> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const data: { job_id: string; candidate_ids?: string[] } = { job_id: jobID };
    if (candidateIDs && candidateIDs.length > 0) {
      data.candidate_ids = candidateIDs;
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/employer/calculate-similarity-score-batch`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data,
      timeout: 30000,
    });

    // The endpoint returns { job_id, job_title, scores: [...] }; callers want the list.
    return response.data.data?.scores ?? response.data.data;
  } catch (error) {
    const err = error as AxiosError;
    console.error("Failed to get batch calculated similarity scores:", err);
    return undefined;
  }
}

export async function calculateSimiliarityScore(
  jobID: string,
  userID: string
): Promise<CalculateSimiliarityScoreResponse | undefined> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/employer/calculate-similarity-score`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      params: { job_id: jobID, candidate_id: userID },
      timeout: 10000, // 10 second timeout
    });

    // The API returns the applications array directly
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError;
    console.error("Failed to get calculated similiarity score:", err);

    // Return empty array on error
    return undefined;
  }
}

export interface JobMatchAnalysis {
  similarity: number;
  suitability_analysis: {
    match_explanation: string;
    job_strengths: string[];
    job_challenges: string[];
  };
}

/**
 * Analyzes how well a job matches the user's CV
 * @param jobId - The UUID of the job to analyze
 * @returns Promise with job suitability analysis data
 */
export async function analyzeJobMatch(
  jobId: string
): Promise<JobMatchAnalysis> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/search/jobs/${jobId}/analyze`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 15000, // 15 second timeout
    });

    const data = {
      similarity: response.data.job.similarity,
      suitability_analysis: response.data.suitability_analysis,
    };

    return data;
  } catch (error) {
    const err = error as AxiosError;
    console.error("failed to analyzed job", err);
    return {} as JobMatchAnalysis;
  }
}

export async function inviteToApply(params: InviteToApplyRequest): Promise<InviteToApplyResponse> {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/invite-to-apply`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: params,
      timeout: 12000, // 10 second timeout
    });

    // The API returns the applications array directly
    return {
      code: response.data.code,
      data: response.data.data,
      message: response.data.message,
    };
  } catch (error) {
    const err = error as AxiosError;
    console.error("Failed to invite to apply:", err);
    // Console-only until now, and this resolves rather than rejects, so the
    // caller's `.catch()` never runs: a sourced candidate is simply never
    // invited while the drawer closes as if the invitation went out.
    fatalErrorLog(
      "[job] invite-to-apply failed — the candidate was never invited",
      {
        http_status: err.response?.status ?? "none (network or timeout)",
        detail: (err.response?.data as { message?: string } | undefined)?.message || err.message,
      },
      { area: "invite_to_apply" },
    );

    // Return empty array on error
    return {
      code:
        (error as AxiosError<{ code: number; message: string }>)?.response?.data
          ?.code ?? 500,
      data: undefined,
      message:
        (error as AxiosError<{ code: number; message: string }>)?.response?.data
          ?.message || "Something wrong. Please try again",
    };
  }
}

export async function searchGenerateCandidateExplanation(
  job_title: string,
  candidate_ids: string[]
): Promise<any> {
  const token = getCookie("authToken");
  // The API returns the applications array directly
  return await axios({
    url: `${apiURL}/search/generate-candidate-explanations`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { job_title: job_title, candidate_ids: candidate_ids },
    timeout: 12000, // 10 second timeout
  });
}

export const getSuggestedCandidates = async (
  params: JobSuggestedCandidateParams
): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/jobs/${params.job_id}/suggested-candidates`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params: params,
    timeout: 300000, // 15 second timeout
  });
};

export const getJobInsights = async (jobID: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/jobs/${jobID}/insights`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    timeout: 300000, // 30 second timeout
  });
};

export async function getStartupCandidates(
  params?: StartupCandidateParams
): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/search/startup`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: params,
  });
}

export async function setApplicationStatus(
  application_id: string,
  status: "CV Rejected" | "Assessment Rejected" | "AI Interview Rejected" | string
): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/applications/${application_id}/status`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { status },
  });
}

export const getCompetitiveIntelligence = async (jobID: string, userID: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/global-scores/candidate/${userID}/job/${jobID}/top-n`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};


export async function getJobListing(params: JobListingFilter): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/hr/listings`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params,
    timeout: 15000, // 15 second timeout
  });
}

export async function resendInvitationJobCollaboration(params: ResendInvitationJobCollaboration): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/collaboration/re-invite`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: params,
  });
}

export async function removeJobCollaboration(params: RemoveJobCollaboration): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/collaboration/remove`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: params,
  });
}

export async function getListJob(): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/list`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function shortlistInvite(params: ShortlistInviteRequest): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/shortlist-candidate`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: params,
  });
}

export async function removeShortlistInvite(params: ShortlistInviteRequest): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/shortlist-candidate-remove`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: params,
  });
}

export async function updateShortlistNote(shortlistId: string, notes: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/shortlist-candidate/${shortlistId}`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { notes },
  });
}

export async function deleteShortlistCandidate(shortlistId: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/shortlist-candidate/${shortlistId}`,
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getApplicantInsight(job_id: string, days?: string): Promise<any> {
  const token = getCookie("authToken");
  const params: Record<string, string> = {};
  if (days && days !== "all") {
    params.days = days;
  }

  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicants/insight`,
    method: "GET",
    params,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getJobVersions(job_id: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/${job_id}/versions`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getGenerateScoreExplanation(
  jobID: string,
  userID: string
): Promise<any> {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/generate-score-explanation`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { job_id: jobID, candidate_id: userID },
  });
}

export async function getScoreHistory(
  candidateId: string,
  jobId: string
): Promise<any> {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/score-history`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params: { candidate_id: candidateId, job_id: jobId },
  });
}

export async function getJobMetrics(periode: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/metrics`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params: {
      periode
    },
  });
}

export async function getApplicantMetrics(periode: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/applicant/metrics`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params: {
      periode
    },
  });
}

export async function applicantUpdateStatus(job_id: string, payload: ApplicantUpdateStatusRequest): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/update-status`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload,
  });
}

export async function getApplicantCount(job_id: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicants-count`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getJobApplicantInsight(job_id: string, applicant_id: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/${applicant_id}/generate-insights`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getCandidateActivityHistories(job_id: string, applicant_id: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/${applicant_id}/activity-history`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getJobListApplicantEmail(job_id: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/${job_id}/list-email-applicants`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function updateApplicantStatusAssessmentCompletion(job_id: string, applicant_id: string, status: string): Promise<any> {
  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/${applicant_id}/assessment-completion`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    data: { status },
  });
}

export async function applicantRequestNewAssessmentLink(job_id: string, applicant_id: string): Promise<any> {
  const token = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/${applicant_id}/request-new-assessment-link`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function updateApplicantScheduleNote(job_id: string, applicant_id: string, payload: UpdateApplicantScheduleNote): Promise<any> {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/${applicant_id}/update-schedule-note`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload,
  });
}

export async function addMultipleApplicantScheduleNote(job_id: string, payload: AddMultipleApplicantScheduleNote): Promise<any> {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/add-schedule-note`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload,
  });
}

export async function getApplicantReviewScheduled(job_id: string, applicant_id: string): Promise<any> {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/${applicant_id}/review-scheduled`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function approveAssessmentLink(job_id: string, applicant_id: string): Promise<any> {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/${applicant_id}/approve-request-assessment-link`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getJobApplicantStatus(job_id: string): Promise<any> {
  const token = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/status`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function applicantStartInterview(job_id: string, applicant_id: string): Promise<any> {
  const token = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicant/${applicant_id}/start-interview`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getListJobApplicant(candidate_id: string): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/applicant/${candidate_id}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getJobApplicantGroup(job_id: string, application_status: string = "", funnel_status: string = ""): Promise<any> {

  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicants/group`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params: {
      application_status,
      funnel_status,
    }
  });
}

export async function deleteApplicants(job_id: string, applicant_ids: string[]): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicants/delete`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { applicant_ids },
  });
}

export async function restoreApplicants(job_id: string, applicant_ids: string[]): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/${job_id}/applicants/restore`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { applicant_ids },
  });
}

// Screening invitation over WhatsApp (#3478, BE #4533). The FE sends ONLY
// job_id + applicant_ids: the Twilio content template and every variable
// (first name / company / job title) are resolved server-side, like every other
// templated send in this product. The send endpoint also writes the
// "Pending Assessment" transition and the activity log itself — the caller must
// NOT additionally call applicantUpdateStatus (unlike the email path).

/** Why a candidate cannot be invited over WhatsApp. Codes come from BE #4533. */
export type ScreeningInvitationIneligibleReason =
  | "missing_phone_number"
  | "invalid_phone_number"
  | "already_invited"
  | "wa_not_enabled"
  | "template_unavailable";

export interface ScreeningInvitationPreviewMessage {
  applicant_id: string;
  phone_number: string | null;
  /** The approved template body, fully rendered by the BE — display verbatim. */
  text: string | null;
  eligible: boolean;
  reason: ScreeningInvitationIneligibleReason | null;
}

export interface ScreeningInvitationPreviewResult {
  template_event: string;
  messages: ScreeningInvitationPreviewMessage[];
}

export interface ScreeningInvitationSendResult {
  sent: { applicant_id: string; phone_number: string }[];
  failed: { applicant_id: string; reason: ScreeningInvitationIneligibleReason }[];
}

/** POST /jobs/{job_id}/applicants/screening-invitation/whatsapp/preview — side-effect free. */
export async function previewScreeningInvitationWhatsapp(
  job_id: string,
  applicant_ids: string[]
): Promise<ScreeningInvitationPreviewResult> {
  const token = getCookie("authToken");

  const response: AxiosResponse = await axios({
    url: `${apiURL}/jobs/${job_id}/applicants/screening-invitation/whatsapp/preview`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { applicant_ids },
    timeout: 30000,
  });

  return response.data?.data as ScreeningInvitationPreviewResult;
}

/** POST /jobs/{job_id}/applicants/screening-invitation/whatsapp — dispatches + moves to Pending Assessment. */
export async function sendScreeningInvitationWhatsapp(
  job_id: string,
  applicant_ids: string[]
): Promise<ScreeningInvitationSendResult> {
  const token = getCookie("authToken");

  const response: AxiosResponse = await axios({
    url: `${apiURL}/jobs/${job_id}/applicants/screening-invitation/whatsapp`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { applicant_ids },
    // Bulk dispatch to Twilio runs inline — a 15s ceiling aborted real sends.
    timeout: 60000,
  });

  return response.data?.data as ScreeningInvitationSendResult;
}

export async function getJobFilter(): Promise<any> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/filter-options`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

// ─── Application Comments ───────────────────────────────────────────────────
//
// Wraps GET / POST / PATCH / DELETE on
// /jobs/applications/{application_id}/comments(/{comment_id}).
// All endpoints require a Bearer token. PATCH/DELETE return 403 when the
// caller is not the original commenter and 404 when the comment is missing —
// callers should branch on `error.response?.status` to surface the right
// snackbar.

export async function listApplicationComments(
  application_id: string
): Promise<AxiosResponse<{ code: number; message: string; data: ApplicationComment[] }>> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/applications/${application_id}/comments`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createApplicationComment(
  application_id: string,
  body: CreateApplicationCommentRequest
): Promise<AxiosResponse<{ code: number; message: string; data: ApplicationComment }>> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/applications/${application_id}/comments`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: body,
  });
}

export async function updateApplicationComment(
  application_id: string,
  comment_id: string,
  body: UpdateApplicationCommentRequest
): Promise<AxiosResponse<{ code: number; message: string; data: ApplicationComment }>> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/applications/${application_id}/comments/${comment_id}`,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: body,
  });
}

export async function deleteApplicationComment(
  application_id: string,
  comment_id: string
): Promise<AxiosResponse<{ code: number; message: string }>> {
  const token = getCookie("authToken");

  return await axios({
    url: `${apiURL}/jobs/applications/${application_id}/comments/${comment_id}`,
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}
// ── Applicant Eligibility Check (BE branch feat/applicant-eligibility-check) ──
// GET /jobs/{job_id}/applicant/{applicant_id}/eligibility-check — HR-side view
// of the candidate's eligibility answers for one application. Roles: hr (job
// owner), admin, super_admin; job_seeker → 403.
// NOTE the BE key is spelled `elligibility_answers` (matches the stored shape);
// it is null until the candidate submits.
export interface ApplicantEligibilityCheck {
  applicant_id: string;
  job_id: string;
  eligibility_check_enabled: boolean;
  is_completed: boolean;
  work_authorization_metadata: JobWorkAuthorizationMetadata | null;
  elligibility_answers: SurveyFormData | null;
  location_metadata: LocationMetadata | null;
}

// Returns null on 403/404 (no permission / job or application not found) so the
// caller can simply hide the Eligibility Check tab.
export async function getApplicantEligibilityCheck(
  jobId: string,
  applicantId: string
): Promise<ApplicantEligibilityCheck | null> {
  const token = getCookie("authToken");
  try {
    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/${jobId}/applicant/${applicantId}/eligibility-check`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 30000,
    });
    return response.data?.data ?? null;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 403 || status === 404) return null;
    catchErrorLog(`Failed to get applicant eligibility check: ${error}`);
    return null;
  }
}
